import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Slide } from '@mui/material';
import { Icon } from '@iconify/react';
import {
    updateOccupant,
    getOccupantDetails,
    uploadOccupantDocument,
    deleteOccupantDocument
} from '../../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { getFlatsDropdown } from '../../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import SearchableDropdown from '../../../../../components/SearchableDropdown';
import MemberRoleSelect from '../../../../../components/MemberRoleSelect';
import usePermissions from '../../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../../consts/permissions';
import { selectedSocietyStorer } from '../../../../../utils/webStorer';
import fetchCurrentRoleId from '../../../../../utils/helpers/fetchCurrentRole';
import { TENANT_DOCUMENT_SLOTS, absoluteFileUrl } from './tenancyStatus';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});


const ACCEPT_TYPES = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx';


/**
 * Renders a slot per document type. When a slot has an existing upload we
 * show the file link + delete; otherwise we show a small drop/browse zone.
 * The parent is responsible for dispatching upload/delete actions and passing
 * loading state through so the UI can disable buttons during in-flight requests.
 */
const TenantDocumentsSection = ({ occupantId, documents, uploading, deleting, onUpload, onDelete }) => {
    // Only tenants that have been saved (have an id) can attach docs. If the
    // parent is somehow rendering us pre-save, disable everything and hint.
    const canUpload = !!occupantId;

    return (
        <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <Icon icon="mdi:folder-multiple-outline" className="tw-text-blue-500 tw-text-sm" />
                <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">
                    Documents <span className="tw-text-gray-400 tw-font-normal tw-normal-case">(Optional)</span>
                </span>
            </div>
            {!canUpload && (
                <p className="tw-text-[11px] tw-text-amber-600 tw-mb-2">
                    Save the tenant first, then re-open Edit to upload documents.
                </p>
            )}
            <div className="tw-grid tw-grid-cols-1 tw-gap-2">
                {TENANT_DOCUMENT_SLOTS.map((slot) => {
                    const existing = (documents || []).filter(d => d.document_type === slot.key);
                    return (
                        <div
                            key={slot.key}
                            className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-2.5 tw-flex tw-items-center tw-gap-3"
                        >
                            <Icon icon={slot.icon} className="tw-text-blue-500 tw-text-base tw-flex-shrink-0" />
                            <div className="tw-flex-1 tw-min-w-0">
                                <div className="tw-text-xs tw-font-medium tw-text-gray-700">{slot.label}</div>
                                {existing.length > 0 ? (
                                    <ul className="tw-mt-1 tw-space-y-0.5">
                                        {existing.map((doc) => (
                                            <li key={doc.document_id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-[11px] tw-text-gray-600">
                                                <a
                                                    href={absoluteFileUrl(doc.file_path)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="tw-text-blue-600 hover:tw-underline tw-truncate"
                                                    title={doc.file_name}
                                                >
                                                    <Icon icon="mdi:paperclip" className="tw-inline tw-text-xs tw-mr-0.5" />
                                                    {doc.file_name}
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={() => onDelete(doc)}
                                                    disabled={deleting}
                                                    className="tw-text-red-500 hover:tw-text-red-700 tw-p-0.5 disabled:tw-opacity-50"
                                                    title="Remove"
                                                >
                                                    <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5">No file uploaded</p>
                                )}
                            </div>
                            <label
                                className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-[11px] tw-rounded-md tw-border tw-border-blue-200 tw-text-blue-700 hover:tw-bg-blue-50 tw-cursor-pointer ${(!canUpload || uploading) ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                            >
                                <Icon icon="mdi:upload-outline" className="tw-text-sm" />
                                {existing.length > 0 ? 'Replace' : 'Upload'}
                                <input
                                    type="file"
                                    accept={ACCEPT_TYPES}
                                    disabled={!canUpload || uploading}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) onUpload(slot.key, file);
                                        // Reset so the same file can be re-selected after delete.
                                        e.target.value = '';
                                    }}
                                    className="tw-hidden"
                                />
                            </label>
                        </div>
                    );
                })}
            </div>
            <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1.5">PDF, image, or Word up to 10 MB.</p>
        </div>
    );
};

const EditTenantModal = ({
    open,
    handleClose,
    member,
    wings = [],
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const {
        updateLoading,
        occupantDetails,
        detailsLoading,
        documents = [],
        documentUploadLoading,
        documentDeleteLoading
    } = useSelector(state => state.OccupantReducer);
    const { flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers);
    const { can } = usePermissions();
    // Anyone who may add / edit members picks the login role; the options
    // (and the API) limit non-admins to member roles.
    const canPickRole = can(PERMISSIONS.USER_MANAGE_ROLES) || can(PERMISSIONS.OCCUPANT_UPDATE);

    const modalFlats = flatsDropdown || [];

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        wing_id: 'all',
        flat_id: 'all',
        is_resident: false,
        start_date: '',
        agreement_end_date: '',
        role_id: '',
    });
    const [errors, setErrors] = useState({});

    // Fetch occupant details when modal opens
    useEffect(() => {
        if (member && open) {
            dispatch(getOccupantDetails({ id: member.occupant_id, type: 'tenant' }));
        }
        setErrors({});
    }, [member, open, dispatch]);

    // Populate form when details are fetched
    useEffect(() => {
        if (occupantDetails && open) {
            const details = occupantDetails;
            setFormData((prev) => ({
                ...prev,
                first_name: details.first_name || '',
                last_name: details.last_name || '',
                email: details.email || '',
                phone: details.phone || '',
                date_of_birth: details.date_of_birth ? details.date_of_birth.split('T')[0] : '',
                wing_id: details.flat?.wing?.wing_id || details.flat?.wing_id || 'all',
                flat_id: details.flat?.flat_id || details.flat_id || 'all',
                is_resident: !!details.is_resident,
                start_date: details.start_date ? details.start_date.split('T')[0] : '',
                agreement_end_date: details.agreement_end_date ? details.agreement_end_date.split('T')[0] : '',
            }));
            const wingId = details.flat?.wing?.wing_id || details.flat?.wing_id;
            if (wingId) {
                dispatch(getFlatsDropdown({ wing_id: wingId }));
            }
        }
    }, [occupantDetails, open, dispatch]);

    // Preselect the tenant's current login role from user-societies.
    useEffect(() => {
        if (!open || !canPickRole || !member) return;
        const societyId = selectedSocietyStorer.getSocietyId();
        let cancelled = false;
        fetchCurrentRoleId({
            societyId,
            sourceType: 'OCCUPANCY',
            sourceRowId: member.occupant_id,
        }).then((roleId) => {
            if (!cancelled && roleId) {
                setFormData((prev) => ({ ...prev, role_id: roleId }));
            }
        });
        return () => { cancelled = true; };
    }, [open, canPickRole, member]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleWingChange = (value) => {
        setFormData(prev => ({ ...prev, wing_id: value, flat_id: 'all' }));
        if (value !== 'all') {
            dispatch(getFlatsDropdown({ wing_id: value }));
        } else {
            dispatch(getFlatsDropdown({}));
        }
    };

    const handleFlatChange = (value) => {
        setFormData(prev => ({ ...prev, flat_id: value }));
    };

    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role_id: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.first_name.trim()) newErrors.first_name = 'First Name is required';
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone is required';
        } else if (!/^\d{10}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Phone must be exactly 10 digits';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const payload = {
            id: member.occupant_id,
            type: 'TENANT',
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email || undefined,
            phone: formData.phone,
            is_resident: !!formData.is_resident,
        };
        if (formData.flat_id !== 'all') payload.flat_id = formData.flat_id;
        if (formData.date_of_birth) payload.date_of_birth = formData.date_of_birth;
        if (formData.start_date) payload.start_date = formData.start_date;
        // Send agreement_end_date explicitly (including as `null`) so clearing
        // the field on edit persists — service reads `!== undefined`.
        payload.agreement_end_date = formData.agreement_end_date || null;
        if (canPickRole && formData.role_id) payload.role_id = Number(formData.role_id);

        payload.onSuccess = () => {
            handleClose();
            if (onSuccess) onSuccess();
        };

        dispatch(updateOccupant(payload));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
            <div className="tw-bg-white tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
                {/* Compact Header */}
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-50 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:account-edit-outline" className="tw-text-blue-600 tw-text-base" />
                        </div>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Edit Tenant</h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="tw-w-7 tw-h-7 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-transition-colors"
                    >
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                {/* Loading State */}
                {detailsLoading ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-blue-600 tw-border-t-transparent"></div>
                        <p className="tw-text-sm tw-text-gray-500 tw-mt-3">Loading details...</p>
                    </div>
                ) : (
                    /* Form */
                    <form onSubmit={onSubmit} className="tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
                        <div className="tw-px-4 tw-py-3 tw-space-y-3 tw-overflow-y-auto tw-flex-1">
                            {/* Personal Info */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:account-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Personal Information</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                            First Name <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.first_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="First Name"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            maxLength={50}
                                        />
                                        {errors.first_name && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.first_name}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                            Last Name
                                        </label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.last_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Last Name"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            maxLength={50}
                                        />
                                        {errors.last_name && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.last_name}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:phone-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Contact Details</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                            Phone <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="phone"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.phone ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Phone Number"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            maxLength={15}
                                        />
                                        {errors.phone && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.phone}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className={`tw-w-full tw-px-3 tw-py-1.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Email Address"
                                            value={formData.email}
                                            onChange={handleChange}
                                            maxLength={100}
                                        />
                                        {errors.email && <p className="tw-text-red-500 tw-text-[11px] tw-mt-0.5">{errors.email}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Date of Birth</label>
                                        <input
                                            type="date"
                                            name="date_of_birth"
                                            className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                            value={formData.date_of_birth}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Unit Details */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                    <Icon icon="mdi:home-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Unit Details</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Wing</label>
                                        <SearchableDropdown
                                            value={formData.wing_id}
                                            onChange={handleWingChange}
                                            options={wings}
                                            valueKey="wing_id"
                                            labelKey="title"
                                            placeholder="Select Wing"
                                            className="tw-w-full"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Flat</label>
                                        <SearchableDropdown
                                            value={formData.flat_id}
                                            onChange={handleFlatChange}
                                            options={modalFlats}
                                            valueKey="flat_id"
                                            labelKey="flat_number"
                                            placeholder="Select Flat"
                                            loading={flatsDropdownLoading}
                                            className="tw-w-full"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Start Date</label>
                                        <input
                                            type="date"
                                            name="start_date"
                                            className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                            value={formData.start_date}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Agreement End Date</label>
                                        <input
                                            type="date"
                                            name="agreement_end_date"
                                            min={formData.start_date || undefined}
                                            className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                            value={formData.agreement_end_date}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="tw-flex tw-items-center tw-gap-3 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-border tw-border-gray-100">
                                        <label className="tw-relative tw-inline-flex tw-items-center tw-cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="is_resident"
                                                className="tw-sr-only tw-peer"
                                                checked={formData.is_resident}
                                                onChange={handleChange}
                                            />
                                            <div className="tw-w-9 tw-h-5 tw-bg-gray-300 tw-rounded-full peer-checked:tw-bg-emerald-500 tw-transition-colors after:tw-content-[''] after:tw-absolute after:tw-top-0.5 after:tw-left-0.5 after:tw-bg-white after:tw-rounded-full after:tw-h-4 after:tw-w-4 after:tw-transition-transform peer-checked:after:tw-translate-x-4"></div>
                                        </label>
                                        <span className={`tw-text-sm tw-font-medium ${formData.is_resident ? 'tw-text-emerald-600' : 'tw-text-gray-500'}`}>
                                            {formData.is_resident ? 'Residing' : 'Non-Residing'}
                                        </span>
                                    </div>
                                    {canPickRole && (
                                        <div className="tw-col-span-2">
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Login Role</label>
                                            <MemberRoleSelect
                                                value={formData.role_id}
                                                onChange={handleRoleChange}
                                                placeholder="Default: Resident"
                                                className="tw-w-full"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Documents — Police NOC + Rental Agreement */}
                            <TenantDocumentsSection
                                occupantId={member?.occupant_id}
                                documents={documents}
                                uploading={documentUploadLoading}
                                deleting={documentDeleteLoading}
                                onUpload={(docType, file) => dispatch(uploadOccupantDocument({
                                    occupant_id: member?.occupant_id,
                                    document_type: docType,
                                    file
                                }))}
                                onDelete={(doc) => dispatch(deleteOccupantDocument({
                                    document_id: doc.document_id,
                                    occupant_id: member?.occupant_id
                                }))}
                            />
                        </div>

                        {/* Footer */}
                        <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={updateLoading}
                                className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={updateLoading}
                                className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50"
                            >
                                {updateLoading ? (
                                    <>
                                        <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:check" className="tw-text-base" />
                                        Update Tenant
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Dialog>
    );
};

export default EditTenantModal;
